import React from "react";
import { Alert, Box, Button, Checkbox, Chip, FormControlLabel, FormGroup, IconButton, Link, Paper, TextField, Tooltip, Typography } from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import AddIcon from "@mui/icons-material/Add";
import UploadIcon from "@mui/icons-material/Upload";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PauseCircleOutlineIcon from "@mui/icons-material/PauseCircleOutline";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import SystemUpdateAltIcon from "@mui/icons-material/SystemUpdateAlt";
import PowerSettingsNewIcon from "@mui/icons-material/PowerSettingsNew";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import StopCircleOutlinedIcon from "@mui/icons-material/StopCircleOutlined";
import ScheduleIcon from "@mui/icons-material/Schedule";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import globals from "../../Globals";
import { replaceDeep } from "../../Layout/Router";
import { PageContainer, SectionHeader, SelectField, FormDialog, DataTable } from "../../Layout/Common";
import { gs, Loader, StaleHint, loadOptions, ensureOptions, regionLabel, regionOptions, planText, canEdit, canUploadLauncher, isId, StateChip, STATES, IdText, Cut, UsageBar,
         FIXED, ELLIPSIS, MONO, UPLOAD_TIMEOUT, ago, ageSec, when, fromUnix, uptime, toLocalInput, fromLocalInput, n, usdH, usdDay, pct, mb,
         branchShort, majorMinor, versionLabel, shortId, openLogs } from "./servers";

// Game Servers → Machines: the title's VPSs (`servers machines`, polled every 5 s) and what an operator does with them:
// deploy (Vultr), drain / undrain, graceful shutdown, forced destroy, launcher update, launcher upload. A machine opens
// its detail (deep link /<game>/servers/machines/<machineId>[/<serverId>]): its servers (`servers servers`, with the
// per-server drain / stop / scheduled stop / restart) and the log viewer (`servers machine-logs`, pushed to the launcher).
// Worlds (persistent servers) never drain: a draining machine keeps its world servers until the operator stops each one
// (now or at a time), which places the world's successor on another machine.

const LINES = [100, 200, 500, 1000, 2000, 5000];
const LAUNCHER = "launcher";   // the log source select's value for the launcher's own log (serverId null)
const ANY = "*";
const ALIVE_WARN_SEC = 30, ALIVE_LOST_SEC = 120;

// Last keep-alive: a warning past 30 s, an error past the 2 minutes that make a machine lost
function LastAlive({ m }) {
    const s = ageSec(m.lastAlive);
    if (s == null) return <Typography variant="body2" color="text.secondary">never</Typography>;
    const color = s > ALIVE_LOST_SEC ? "error.main" : s > ALIVE_WARN_SEC ? "warning.main" : "text.primary";
    return <Tooltip title={`last keep-alive ${when(m.lastAlive)}`}><Typography variant="body2" sx={{ fontSize: 13, color, fontWeight: s > ALIVE_WARN_SEC ? 600 : 400 }}>{ago(m.lastAlive)}</Typography></Tooltip>;
}

// The machine's state plus what the backend wants from it (draining, shutdown, update, destroy)
function MachineState({ m }) {
    return <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
        <StateChip kind="machine" state={m.state} title={`since ${when(m.stateChangedAt)}`} />
        {m.draining && <Tooltip title={`Draining: no new servers or sessions are placed here; its pooled servers drain as they go idle${m.worlds > 0 ? `. ${m.worlds} world server(s) keep running until you schedule their stop` : ""}`}><Chip size="small" color="warning" variant="outlined" label="draining" /></Tooltip>}
        {m.forceDestroy
            ? <Chip size="small" color="error" variant="outlined" label="→ destroy" />
            : m.desired && m.desired !== "run" && <Chip size="small" variant="outlined" label={`→ ${m.desired}`} />}
    </Box>;
}

// World (persistent) servers running on the machine; on a draining machine they are what it still waits for
function WorldsCount({ m }) {
    const w = m.worlds ?? 0;
    if (!(m.draining && w > 0)) return <span>{n(w)}</span>;
    return <Tooltip title="Draining, but worlds never drain: schedule a stop on each world's server (machine detail) to move it to another machine">
        <Box component="span" sx={{ color: "warning.main", fontWeight: 600 }}>{n(w)}</Box>
    </Tooltip>;
}

// The per-machine action buttons (list rows and the detail header)
function MachineActions({ m, act, onDestroy }) {
    const edit = canEdit();
    const btn = (title, icon, onClick, disabled, color) => (
        <Tooltip title={title}><span><IconButton size="small" color={color} disabled={!!disabled} onClick={e => { e.stopPropagation(); onClick(); }}>{icon}</IconButton></span></Tooltip>
    );
    const running = m.desired === "run" && !m.forceDestroy;
    return <Box sx={{ display: "flex", gap: 0.25 }}>
        {edit && (m.draining
            ? btn("Undrain: take new servers and sessions again (a draining server still finishes draining)", <PlayCircleOutlineIcon fontSize="small" />, () => act("Undraining the machine", "machine-undrain", m), !running)
            : btn("Drain: no new servers or sessions here; its pooled servers drain, its world servers keep running", <PauseCircleOutlineIcon fontSize="small" />,
                () => act("Draining the machine", "machine-drain", m,
                    `Drain machine ${shortId(m.id)} (${m.region}, ${m.ip ?? "no ip"})?\n\nNo new servers or sessions are placed there. Its pooled servers (matches, rooms) take no new sessions and stop once their sessions end.` +
                    (m.worlds > 0
                        ? `\n\nIts ${m.worlds} world server(s) keep running: a world never drains. To empty the machine, schedule a stop on each world's server (machine detail → Schedule stop): at that time the world moves to a new server on another machine.`
                        : "\n\nIt runs no world server, so it empties on its own.")),
                !running))}
        {edit && btn("Update launcher: its servers STOP first, then the launcher self-updates (GameLauncher.zip) and registers again",
            <SystemUpdateAltIcon fontSize="small" />, () => act("Updating the launcher", "machine-update-launcher", m,
                `Update the launcher of machine ${shortId(m.id)} (${m.region}, ${m.ip ?? "no ip"})?\n\nEVERY server on it stops first (${m.sessions ?? 0} sessions, ${m.players ?? 0} players), then the launcher downloads GameLauncher.zip, restarts and registers again. The planner then places servers again.`),
            !(running && (m.state === "ready" || m.state === "starting-up")))}
        {edit && btn("Shut down (graceful): every server stops with its deadline, then the VPS is destroyed", <PowerSettingsNewIcon fontSize="small" />,
            () => act("Shutting the machine down", "machine-shutdown", m,
                `Shut down machine ${shortId(m.id)} (${m.region}, ${m.ip ?? "no ip"})?\n\nEvery server stops gracefully${m.worlds > 0 ? `, its ${m.worlds} world server(s) included` : ""} (saves, ${m.sessions ?? 0} sessions end, ${m.players ?? 0} players are disconnected), the launcher reports stopped and the VPS is destroyed. A provisioning or lost machine is destroyed at once.`),
            !running, "warning")}
        {edit && btn("Destroy now (forced): the VPS is deleted whatever its servers are doing", <DeleteForeverIcon fontSize="small" />, () => onDestroy(m), m.forceDestroy, "error")}
    </Box>;
}

export default class MachinesPage extends React.Component {
    state = {
        machines: null, loadedAt: null, stale: null, stateFilter: ANY,
        selected: globals.route?.deep?.[0] || null, selectedServer: globals.route?.deep?.[1] || null,
        options: null, specs: null,
        deploy: null,            // { region, plan, branches: [] } while the dialog is open
        upload: null,            // { file } while the dialog is open
        destroy: null,           // { machine, typed }
        busy: false,
    };

    loader = new Loader(this, async () => {
        ensureOptions(this);
        const { stateFilter } = this.state;
        const r = await gs("Loading machines", ["machines"], { payload: stateFilter !== ANY ? { state: stateFilter } : {}, silent: true });
        return { machines: r?.machines ?? [] };
    });

    componentDidMount() {
        this.loader.start();
    }
    componentWillUnmount() { this.loader.stop(); }

    select = (id, serverId = null) => {
        this.setState({ selected: id, selectedServer: serverId });
        replaceDeep(id ? [id, serverId] : []);
    };

    // A machine action: an optional confirm, the toast, the fresh list from its answer
    act = (description, command, m, question) => {
        if (question && !window.confirm(question)) return Promise.resolve();
        return gs(description, [command, m.id])
            .then(r => { if (r?.machines) this.setState({ machines: this.state.stateFilter === ANY ? r.machines : r.machines.filter(x => x.state === this.state.stateFilter) }); })
            .catch(() => {});
    };

    // ----- deploy -----
    openDeploy = () => {
        this.setState({ deploy: { region: "", plan: "", branches: [] }, deployError: null });
        Promise.all([loadOptions(), gs("Loading specs", ["specs"], { silent: true })])
            .then(([options, s]) => {
                const specs = s?.specs ?? [];
                const plan = specs.some(x => x.plan === options?.defaultPlan) ? options.defaultPlan : (specs[0]?.plan ?? "");
                this.setState(st => ({ options, specs, deploy: st.deploy && { ...st.deploy, region: st.deploy.region || options?.defaultRegion || "", plan: st.deploy.plan || plan } }));
            })
            .catch(e => this.setState({ deployError: e?.message || "failed" }));
    };
    setDeploy = (patch) => this.setState(st => ({ deploy: { ...st.deploy, ...patch } }));
    planAvailable = (planId, region) => {
        const p = this.state.options?.plans?.find(x => x.id === planId);
        return !p || !region || !(p.locations?.length) || p.locations.includes(region);
    };
    doDeploy = () => {
        const { region, plan, branches } = this.state.deploy;
        this.setState({ busy: true });
        gs("Deploying a machine", ["machine-deploy"], { payload: { region, plan, branches }, timeout: 60000, retry: false })
            .then(r => { if (r?.machines) this.setState({ machines: r.machines }); this.setState({ deploy: null }); })
            .catch(() => {})
            .finally(() => this.setState({ busy: false }));
    };

    renderDeploy() {
        const { deploy, options, specs, busy, deployError } = this.state;
        if (!deploy) return null;
        const specPlans = (specs ?? []).map(s => {
            const p = options?.plans?.find(x => x.id === s.plan);
            const here = this.planAvailable(s.plan, deploy.region);
            return { value: s.plan, disabled: !here,
                     label: `${s.plan} · ${s.capacity} units · ${usdH(s.hourlyUsd)}${p ? ` · ${p.vcpu} vCPU, ${mb(p.ramMb)}` : ""}${here ? "" : " (not in this region)"}` };
        });
        const branches = options?.branches ?? globals.branches;
        const ok = deploy.region && deploy.plan && deploy.branches.length > 0 && this.planAvailable(deploy.plan, deploy.region) && !busy;
        return (
            <FormDialog open title="Deploy a machine" onClose={() => this.setState({ deploy: null })} onConfirm={this.doDeploy}
                        confirmText="Deploy" confirmDisabled={!ok} maxWidth="sm" fullWidth>
                {deployError
                    ? <Alert severity="error" sx={{ my: 1 }}>Could not load the regions, plans and specs: {deployError}</Alert>
                    : !options || !specs
                    ? <Typography variant="body2" color="text.secondary" sx={{ my: 2 }}>Loading regions, plans and specs…</Typography>
                    : <>
                        {options.vultrError && <Alert severity="warning" sx={{ mb: 1 }}>Vultr's lists could not be read: {options.vultrError}</Alert>}
                        {specs.length === 0 && <Alert severity="info" sx={{ mb: 1 }}>
                            No machine spec yet. A plan needs a spec before it can be deployed (it says how many capacity units the plan carries): add
                            one on the Machine Specs page.
                        </Alert>}
                        <SelectField label="Region" fullWidth sx={{ my: 1 }} value={deploy.region} onChange={v => this.setDeploy({ region: v })}
                                     options={regionOptions(options, [deploy.region, options.defaultRegion])} />
                        <SelectField label="Plan (from the machine specs)" fullWidth sx={{ my: 1 }} value={deploy.plan} onChange={v => this.setDeploy({ plan: v })}
                                     options={specPlans} disabled={specPlans.length === 0} />
                        {deploy.plan && <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
                            {planText(options.plans?.find(p => p.id === deploy.plan))}
                        </Typography>}
                        <Typography variant="subtitle2" sx={{ mt: 1 }}>Branches it serves</Typography>
                        <FormGroup row>
                            {branches.map(b => <FormControlLabel key={b} label={b} control={
                                <Checkbox checked={deploy.branches.includes(b)}
                                          onChange={() => this.setDeploy({ branches: deploy.branches.includes(b) ? deploy.branches.filter(x => x !== b) : [...deploy.branches, b] })} />} />)}
                        </FormGroup>
                        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
                            A new Vultr VPS of this title. It stays <strong>provisioning</strong> until its launcher registers (a VPS that never
                            registers is destroyed after 15 minutes), then the planner places servers of the checked branches on it, up to the
                            spec's capacity units. The plan needs a spec (Machine Specs). It costs the spec's hourly price until it is destroyed.
                        </Typography>
                    </>}
            </FormDialog>
        );
    }

    // ----- launcher upload -----
    doUpload = () => {
        const { file } = this.state.upload;
        this.setState({ busy: true });
        gs("Uploading GameLauncher.zip", ["upload-launcher"], { payload: file, timeout: UPLOAD_TIMEOUT, retry: false })
            .then(() => this.setState({ upload: null }))
            .catch(() => {})
            .finally(() => this.setState({ busy: false }));
    };
    renderUpload() {
        const { upload, busy } = this.state;
        if (!upload) return null;
        return (
            <FormDialog open title="Upload the launcher (GameLauncher.zip)" onClose={() => !busy && this.setState({ upload: null })} onConfirm={this.doUpload}
                        confirmText={busy ? "Uploading…" : "Upload"} confirmDisabled={!upload.file || busy} cancelDisabled={busy} maxWidth="sm" fullWidth>
                <input type="file" accept=".zip" style={{ marginTop: 8 }} onChange={e => this.setState({ upload: { file: e.target.files?.[0] ?? null } })} />
                {upload.file && <Typography variant="body2" sx={{ mt: 1 }}>{upload.file.name} · {mb(Math.round(upload.file.size / 1048576 * 10) / 10)}</Typography>}
                <Alert severity="warning" sx={{ mt: 2 }}>
                    There is ONE GameLauncher.zip for <strong>every title's</strong> machines (the launcher is generic): this upload replaces it
                    for all titles, which is why it needs both the <code>servers</code> and the <code>admin</code> role on this title.
                </Alert>
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
                    Private CDN, cache purged. Running machines keep their launcher until you use <strong>Update launcher</strong> on them (it
                    stops their servers first); new machines boot with the new one.
                </Typography>
            </FormDialog>
        );
    }

    // ----- destroy (forced): typed confirmation -----
    renderDestroy() {
        const { destroy } = this.state;
        if (!destroy) return null;
        const m = destroy.machine, code = String(m.id).slice(-6);
        return (
            <FormDialog open title={`Destroy machine ${shortId(m.id)} now?`} onClose={() => this.setState({ destroy: null })}
                        onConfirm={() => { this.setState({ destroy: null }); this.act("Destroying the machine", "machine-destroy", m); }}
                        confirmText="Destroy now" confirmDisabled={destroy.typed.trim() !== code} maxWidth="sm" fullWidth>
                <Alert severity="error" sx={{ mb: 2 }}>
                    The VPS ({m.region}, {m.plan}, {m.ip ?? "no ip"}) is deleted <strong>whatever its servers are doing</strong>: {m.servers ?? 0} servers,
                    {" "}{m.sessions ?? 0} sessions and {m.players ?? 0} players are cut off without saving. Use <strong>Shut down</strong> for a graceful
                    stop; Destroy is for an unreachable, lost or stuck machine.
                </Alert>
                <TextField fullWidth autoFocus label={`Type ${code} to confirm`} value={destroy.typed}
                           onChange={e => this.setState({ destroy: { ...destroy, typed: e.target.value } })} />
            </FormDialog>
        );
    }

    renderList() {
        const { machines, stateFilter } = this.state;
        const list = machines ?? [];
        const columns = [
            { id: "state", label: "State", width: 118, render: m => <MachineState m={m} /> },
            { id: "where", label: "Machine", width: 110, render: m => <>
                <Tooltip title={`${regionLabel(m.region, this.state.options)} · open its servers and logs`}>
                    <Link component="button" underline="hover" onClick={() => this.select(m.id)} sx={{ ...ELLIPSIS, display: "block", maxWidth: "100%", fontWeight: 600, textAlign: "left" }}>
                        {m.region}{m.local ? " (local)" : ""}
                    </Link>
                </Tooltip>
                <Box sx={{ ...ELLIPSIS, fontSize: 12, color: "text.secondary" }} title={m.plan}>{m.plan}</Box>
              </> },
            { id: "ip", label: "IP · id", width: 112, render: m => <>
                <Box sx={{ ...MONO, ...ELLIPSIS }} title={m.ip ? `${m.ip}:${m.pushPort}` : ""}>{m.ip ?? "—"}</Box>
                <IdText id={m.id} onClick={() => this.select(m.id)} />
              </> },
            { id: "branches", label: "Branches", width: 80, render: m => <Tooltip title={(m.branches ?? []).join(", ")}>
                <Box sx={ELLIPSIS}>{(m.branches ?? []).map(branchShort).join(", ") || "—"}</Box></Tooltip> },
            { id: "capacity", label: "Capacity", width: 78, align: "right", render: m => <>{n(m.used)} / {n(m.capacity)}<UsageBar used={m.used} total={m.capacity} /></> },
            { id: "servers", label: "Servers", width: 62, align: "right", render: m => n(m.servers) },
            { id: "worlds", label: "Worlds", width: 60, align: "right", render: m => <WorldsCount m={m} /> },
            { id: "sessions", label: "Sessions", width: 68, align: "right", render: m => n(m.sessions) },
            { id: "players", label: "Players", width: 62, align: "right", render: m => n(m.players) },
            { id: "load", label: "CPU · mem", width: 88, render: m => m.memTotalMb
                ? <Tooltip title={`cpu ${pct(m.cpu, 1)} % · memory ${mb(m.memUsedMb)} of ${mb(m.memTotalMb)} · disk free ${mb(m.diskFreeMb)}`}>
                    <Box sx={ELLIPSIS}>{pct(m.cpu, 1)} % · {pct(m.memUsedMb, m.memTotalMb)} %</Box></Tooltip>
                : "—" },
            { id: "alive", label: "Last alive · launcher", width: 96, render: m => <>
                <LastAlive m={m} />
                <Box sx={{ fontSize: 12, color: "text.secondary" }}>{m.launcherVersion ? `launcher v${m.launcherVersion}` : "no launcher yet"}</Box>
              </> },
            { id: "cost", label: "Cost", width: 68, align: "right", render: m => <Tooltip title={usdDay(m.hourlyUsd)}><span>{usdH(m.hourlyUsd)}</span></Tooltip> },
            { id: "actions", label: "", width: 136, render: m => <MachineActions m={m} act={this.act} onDestroy={x => this.setState({ destroy: { machine: x, typed: "" } })} /> },
        ];
        const t = list.reduce((a, m) => ({ used: a.used + (m.used ?? 0), capacity: a.capacity + (m.capacity ?? 0), usd: a.usd + (m.hourlyUsd ?? 0) }), { used: 0, capacity: 0, usd: 0 });
        return <>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 1, flexWrap: "wrap" }}>
                <SelectField label="State" size="small" minWidth={160} value={stateFilter}
                             onChange={v => this.setState({ stateFilter: v }, this.loader.reload)}
                             options={[{ value: ANY, label: "Any state" }, ...STATES.machine]} />
                <Typography variant="body2" color="text.secondary">
                    {n(list.length)} machine{list.length === 1 ? "" : "s"} · {n(t.used)} / {n(t.capacity)} units used · {usdH(t.usd)} (≈ {usdDay(t.usd)})
                </Typography>
            </Box>
            {machines == null
                ? <Typography variant="body2" color="text.secondary">Loading…</Typography>
                : list.length === 0
                    ? <Typography variant="body2" color="text.secondary" sx={{ my: 2 }}>No machines{stateFilter !== ANY ? ` in state ${stateFilter}` : ""}.</Typography>
                    : <DataTable columns={columns} rows={list} getRowKey={m => m.id} sx={FIXED} />}
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
                Capacity is in units (a server takes its profile's weight). Sessions and players are what the servers report. Worlds are the
                persistent servers running there: they never drain, so a draining machine with worlds empties only when you schedule a stop on
                each world's server. A machine with no keep-alive for 2 minutes becomes lost; it is kept until destroyed (it may come back).
            </Typography>
        </>;
    }

    render() {
        const { selected, selectedServer, machines, loadedAt, stale } = this.state;
        const machine = selected ? (machines ?? []).find(m => m.id === selected) : null;
        return (
            <PageContainer maxWidth={1420}>
                <SectionHeader title={selected ? "Machine" : "Machines"}>
                    <StaleHint loadedAt={loadedAt} stale={stale} />
                    {!selected && canEdit() && <>
                        <Tooltip title={canUploadLauncher()
                            ? "ONE GameLauncher.zip serves every title's machines: an upload here replaces it for all titles"
                            : "ONE GameLauncher.zip serves every title's machines, so uploading it needs both the servers and the admin role on this title"}>
                            <span><Button variant="outlined" size="small" startIcon={<UploadIcon />} disabled={!canUploadLauncher()}
                                          onClick={() => this.setState({ upload: { file: null } })}>Upload launcher (all titles)</Button></span>
                        </Tooltip>
                        <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={this.openDeploy}>Deploy machine</Button>
                    </>}
                    <Tooltip title="Refresh"><IconButton size="small" onClick={this.loader.load}><RefreshIcon fontSize="small" /></IconButton></Tooltip>
                </SectionHeader>
                {selected
                    ? <MachineDetail key={selected} machineId={selected} machine={machine} listLoaded={machines != null} initialServer={selectedServer} options={this.state.options}
                                     onBack={() => this.select(null)} act={this.act} onDestroy={x => this.setState({ destroy: { machine: x, typed: "" } })}
                                     onLogSource={sid => replaceDeep([selected, sid])} />
                    : this.renderList()}
                {this.renderDeploy()}
                {this.renderUpload()}
                {this.renderDestroy()}
            </PageContainer>
        );
    }
}

// ---------- one machine: its servers and the logs ----------
class MachineDetail extends React.Component {
    state = {
        servers: null, loadedAt: null, stale: null,
        logSource: this.props.initialServer || LAUNCHER, logLines: 200, log: null, logError: null, logLoading: false, logAt: null,
        schedule: null,          // { server, value } while the scheduled-stop dialog is open
    };
    logRef = React.createRef();

    loader = new Loader(this, async () => {
        const r = await gs("Loading servers", ["servers"], { payload: { machineId: this.props.machineId }, silent: true });
        return { servers: r?.servers ?? [] };
    });

    // A malformed id in the link is refused by the backend: nothing to load
    componentDidMount() { if (isId(this.props.machineId)) { this.loader.start(); this.loadLog(); } }
    componentWillUnmount() { this.loader.stop(); this.dead = true; }
    componentDidUpdate(_, prev) {
        // a new log text: show its end
        if (prev.log !== this.state.log && this.logRef.current) this.logRef.current.scrollTop = this.logRef.current.scrollHeight;
    }

    // The server's stop deadline is its profile's (the Server shape does not carry it)
    stopDeadline = (s) => {
        const p = this.props.options?.profiles?.find(x => x.id === s.profile);
        return p?.stopDeadlineSec ? `${p.stopDeadlineSec} s` : "its stop deadline";
    };

    // A server action answers every server of the title: keep this machine's
    serverAct = (description, command, s, body, question) => {
        if (question && !window.confirm(question)) return;
        gs(description, [command, s.id], body ? { payload: body } : {})
            .then(r => { if (r?.servers) this.setState({ servers: r.servers.filter(x => x.machineId === this.props.machineId) }); })
            .catch(() => {});
    };

    loadLog = () => {
        const { logSource, logLines } = this.state;
        const seq = this.logSeq = (this.logSeq ?? 0) + 1;
        this.setState({ logLoading: true });
        gs("Loading the log", ["machine-logs", this.props.machineId], { payload: { serverId: logSource === LAUNCHER ? null : logSource, lines: logLines }, silent: true, retry: false, timeout: 45000 })
            .then(r => { if (!this.dead && seq === this.logSeq) this.setState({ log: r?.text ?? "", logError: null, logAt: Date.now() }); })
            .catch(e => { if (!this.dead && seq === this.logSeq) this.setState({ logError: e?.message || "failed" }); })
            .finally(() => { if (!this.dead && seq === this.logSeq) this.setState({ logLoading: false }); });
    };
    setLog = (patch) => {
        this.setState(patch, this.loadLog);
        if ("logSource" in patch) this.props.onLogSource(patch.logSource === LAUNCHER ? null : patch.logSource);
    };

    renderHeader() {
        const { machine: m, machineId, listLoaded } = this.props;
        if (!isId(machineId)) return <Alert severity="error" sx={{ mb: 2 }}>"{machineId}" is not a machine id (24 hex characters).</Alert>;
        if (!m) return <Alert severity={listLoaded ? "warning" : "info"} sx={{ mb: 2 }}>
            {listLoaded ? <>Machine {machineId} is not in the list (destroyed, or another state filter). Its history is in the <Link component="button" onClick={() => openLogs({ machine: machineId })}>events</Link>.</> : "Loading…"}
        </Alert>;
        const line = (label, value) => <Box sx={{ minWidth: 0 }}><Typography variant="caption" color="text.secondary">{label}</Typography><Box sx={{ fontSize: 14 }}>{value}</Box></Box>;
        return (
            <Paper sx={{ p: 2, mb: 2 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5, flexWrap: "wrap" }}>
                    <Box sx={{ ...MONO, fontSize: 14 }}>{m.id}</Box>
                    <MachineState m={m} />
                    <Box sx={{ flex: 1 }} />
                    <Link component="button" underline="hover" onClick={() => openLogs({ machine: m.id })}>events</Link>
                    <MachineActions m={m} act={this.props.act} onDestroy={this.props.onDestroy} />
                </Box>
                <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(170px, 1fr))", gap: 1.5 }}>
                    {line("Region", regionLabel(m.region, this.props.options) + (m.local ? " (local)" : ""))}
                    {line("Plan", m.plan ?? "—")}
                    {line("Address", <Box component="span" sx={MONO}>{m.ip ? `${m.ip}:${m.pushPort}` : "—"}</Box>)}
                    {line("Branches", (m.branches ?? []).join(", ") || "—")}
                    {line("Capacity", <>{n(m.used)} / {n(m.capacity)} units<UsageBar used={m.used} total={m.capacity} /></>)}
                    {line("Servers · sessions · players", `${n(m.servers)} · ${n(m.sessions)} · ${n(m.players)}`)}
                    {line("Worlds (persistent servers)", <WorldsCount m={m} />)}
                    {line("Launcher", m.launcherVersion ? `v${m.launcherVersion}` : "—")}
                    {line("CPU · memory · disk free", m.memTotalMb ? `${pct(m.cpu, 1)} % · ${mb(m.memUsedMb)} / ${mb(m.memTotalMb)} · ${mb(m.diskFreeMb)}` : "—")}
                    {line("Last alive", <LastAlive m={m} />)}
                    {line("Cost", `${usdH(m.hourlyUsd)} (≈ ${usdDay(m.hourlyUsd)})`)}
                    {line("Created", when(m.createdDate))}
                    {line("Registered", when(m.registeredAt))}
                    {m.vultrId && line("Vultr instance", <Cut text={m.vultrId} sx={MONO} />)}
                    {m.lostAt && line("Lost at", when(m.lostAt))}
                    {m.destroyAttempts > 0 && line("Destroy attempts", `${m.destroyAttempts} (last ${when(m.lastDestroyAttemptAt)})`)}
                </Box>
                {m.draining && m.worlds > 0 && <Alert severity="info" sx={{ mt: 2 }}>
                    This machine is draining but still runs {m.worlds} world server{m.worlds === 1 ? "" : "s"}: worlds never drain. It empties
                    once you <strong>schedule a stop</strong> on each world's server below (or stop it now): at that time the world moves to a new
                    server on another machine and re-opens there.
                </Alert>}
            </Paper>
        );
    }

    renderServers() {
        const { servers } = this.state;
        const edit = canEdit();
        const btn = (title, icon, onClick, disabled, color) => (
            <Tooltip title={title}><span><IconButton size="small" color={color} disabled={!!disabled} onClick={onClick}>{icon}</IconButton></span></Tooltip>
        );
        const columns = [
            { id: "state", label: "State", width: 110, render: s => <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                <StateChip kind="server" state={s.state} title={`since ${when(s.stateChangedAt)}${s.readyAt ? ` · ready at ${when(s.readyAt)}` : ""}`} />
                {s.desired && s.desired !== "run" && <Chip size="small" variant="outlined" label={`→ ${s.desired}`} />}
                {s.noticeAt > 0 && <Tooltip title={`stop scheduled at ${when(fromUnix(s.noticeAt))}`}><Chip size="small" color="warning" variant="outlined" icon={<ScheduleIcon />} label={fromUnix(s.noticeAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })} /></Tooltip>}
              </Box> },
            { id: "id", label: "Server", width: 100, render: s => <IdText id={s.id} /> },
            { id: "profile", label: "Profile", width: 90, render: s => <>
                <Box sx={ELLIPSIS} title={`${s.executable ?? ""} ${(s.args ?? []).join(" ")}`}>{s.profile}</Box>
                {s.persistent && <Tooltip title={`hosts world ${s.sessionId ?? "?"}`}><Chip size="small" variant="outlined" label="world" /></Tooltip>}
              </> },
            { id: "version", label: "Version", width: 90, render: s => <Tooltip title={`compact ${s.version}${s.hash ? ` · sha256 ${s.hash}` : ""}`}><span>{versionLabel(s.branch, s.versionText)}</span></Tooltip> },
            { id: "sessions", label: "Sessions rep / claimed / max", width: 95, align: "right", render: s => `${n(s.sessions)} / ${n(s.sessionsUsed)} / ${n(s.maxSessions)}` },
            { id: "players", label: "Players", width: 58, align: "right", render: s => n(s.players) },
            { id: "ports", label: "Ports", width: 75, render: s => <Cut text={(s.ports ?? []).join(", ")} sx={MONO} /> },
            { id: "pid", label: "PID", width: 55, align: "right", render: s => s.pid || "—" },
            { id: "uptime", label: "Uptime", width: 70, render: s => <Tooltip title={s.startedAt > 0 ? `started ${when(fromUnix(s.startedAt))}` : "not running"}><span>{uptime(s.startedAt)}</span></Tooltip> },
            { id: "restarts", label: "Restarts", width: 62, align: "right", render: s => <Box component="span" sx={{ color: s.restarts > 0 ? "warning.main" : undefined }}>{n(s.restarts)}</Box> },
            { id: "exit", label: "Exit", width: 45, align: "right", render: s => s.exitCode ?? "—" },
            { id: "error", label: "Error", width: 105, render: s => s.error ? <Cut text={s.error} sx={{ color: "error.main" }} /> : "—" },
            { id: "actions", label: "", width: 170, render: s => {
                const live = !["stopped", "failed"].includes(s.state);
                return <Box sx={{ display: "flex", gap: 0.25 }}>
                    {btn("Show its log", <ArticleOutlinedIcon fontSize="small" />, () => this.setLog({ logSource: s.id }))}
                    {edit && (s.persistent
                        ? btn("A world's server never drains: move the world with Stop now or Schedule stop", <PauseCircleOutlineIcon fontSize="small" />, () => {}, true)
                        : btn("Drain: no new sessions; it stops once its sessions end", <PauseCircleOutlineIcon fontSize="small" />,
                            () => this.serverAct("Draining the server", "server-drain", s, null), s.desired !== "run" || !live))}
                    {edit && btn(s.noticeAt > 0 ? "Cancel the scheduled stop" : "Schedule a stop at a time (the server gets a notice: a world can warn its players)",
                        <ScheduleIcon fontSize="small" />,
                        () => s.noticeAt > 0
                            ? this.serverAct("Cancelling the scheduled stop", "server-stop", s, { cancel: true }, `Cancel the stop scheduled at ${when(fromUnix(s.noticeAt))}?`)
                            : this.setState({ schedule: { server: s, value: toLocalInput(new Date(Date.now() + 30 * 60000)) } }),
                        s.desired === "stop", s.noticeAt > 0 ? "warning" : undefined)}
                    {edit && btn("Stop now", <StopCircleOutlinedIcon fontSize="small" />,
                        () => this.serverAct("Stopping the server", "server-stop", s, {},
                            `Stop server ${shortId(s.id)} (${s.profile}, ${s.versionText}) now?\n\nIt saves and exits within ${this.stopDeadline(s)}: ${s.sessions ?? 0} sessions end and ${s.players ?? 0} players are disconnected. ` +
                            (s.persistent ? "The world moves to a new server on its newest patch (on another machine when this one is draining) and re-opens there." : "A pooled fleet refills to its target.")),
                        s.desired === "stop", "warning")}
                    {edit && btn("Restart: a successor (same profile, branch, region, world; newest patch of its major.minor) is placed first, then this one stops",
                        <RestartAltIcon fontSize="small" />,
                        () => this.serverAct("Restarting the server", "server-restart", s, null,
                            `Restart server ${shortId(s.id)} (${s.profile}, ${s.versionText})?\n\nA successor on the newest patch of ${majorMinor(s.versionText)} is placed first (fails when no machine has room), then this server stops: its ${s.sessions ?? 0} sessions end.`),
                        s.desired === "stop")}
                </Box>;
            } },
        ];
        return <>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 1 }}>
                <Typography variant="h6">Servers</Typography>
                <StaleHint loadedAt={this.state.loadedAt} stale={this.state.stale} />
            </Box>
            {servers == null
                ? <Typography variant="body2" color="text.secondary">Loading…</Typography>
                : servers.length === 0
                    ? <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>No servers on this machine.</Typography>
                    : <DataTable columns={columns} rows={servers} getRowKey={s => s.id} sx={{ ...FIXED, mb: 1 }} />}
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 3 }}>
                Sessions: reported by the server / claimed in the backend / its maximum. A crashed server restarts with backoff; a crash loop
                (3 crashes in 2 minutes) makes it failed, and the planner replaces it after 5 minutes.
            </Typography>
        </>;
    }

    renderLog() {
        const { servers, logSource, logLines, log, logError, logLoading, logAt } = this.state;
        const sources = [{ value: LAUNCHER, label: "Launcher" }, ...(servers ?? []).map(s => ({ value: s.id, label: `${s.profile} ${s.versionText} · ${shortId(s.id)} (${s.state})` }))];
        if (!sources.some(x => x.value === logSource)) sources.push({ value: logSource, label: `server ${shortId(logSource)}` });
        return <Paper sx={{ p: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 1, flexWrap: "wrap" }}>
                <Typography variant="h6">Log</Typography>
                <SelectField label="Source" size="small" minWidth={300} value={logSource} onChange={v => this.setLog({ logSource: v })} options={sources} />
                <SelectField label="Lines" size="small" minWidth={100} value={logLines} onChange={v => this.setLog({ logLines: v })} options={LINES} />
                <Button size="small" variant="outlined" startIcon={<RefreshIcon />} onClick={this.loadLog} disabled={logLoading}>{logLoading ? "Loading…" : "Refresh"}</Button>
                {logAt && <Typography variant="caption" color="text.secondary">read {ago(logAt)}</Typography>}
            </Box>
            {logError && <Alert severity="warning" sx={{ mb: 1 }}>{logError}</Alert>}
            <Box ref={this.logRef} component="pre" sx={{ ...MONO, m: 0, p: 1.5, bgcolor: "action.hover", borderRadius: 1, height: 460, overflow: "auto", whiteSpace: "pre-wrap", wordBreak: "break-all" }}>
                {log == null ? (logLoading ? "Loading…" : "") : log || "(empty)"}
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
                Read from the launcher on demand (one file per server, rotated at 10 MB); fails while the launcher is unreachable.
            </Typography>
        </Paper>;
    }

    renderSchedule() {
        const { schedule } = this.state;
        if (!schedule) return null;
        const s = schedule.server, at = fromLocalInput(schedule.value);
        const valid = at > Date.now() / 1000;
        return <FormDialog open title={`Schedule a stop of server ${shortId(s.id)}`} onClose={() => this.setState({ schedule: null })}
                           onConfirm={() => { this.setState({ schedule: null }); this.serverAct("Scheduling the stop", "server-stop", s, { at }); }}
                           confirmText="Schedule" confirmDisabled={!valid} maxWidth="xs" fullWidth>
            <TextField type="datetime-local" fullWidth sx={{ mt: 1 }} label="Stop at (your local time)" value={schedule.value} slotProps={{ inputLabel: { shrink: true } }}
                       onChange={e => this.setState({ schedule: { ...schedule, value: e.target.value } })} />
            {valid && <Typography variant="caption" sx={{ display: "block", mt: 1 }}>{new Date(at * 1000).toUTCString()} · in {Math.round((at - Date.now() / 1000) / 60)} min</Typography>}
            {!valid && <Typography variant="caption" color="error" sx={{ display: "block", mt: 1 }}>Pick a time in the future.</Typography>}
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
                The server gets a <code>notice</code> with that time right away (a persistent world can warn its players) and stops when it comes.
                {s.persistent ? " The world then moves to a new server running the newest patch (on another machine when this one is draining) and re-opens there: this is how a world is updated, or moved off a draining machine." : " A pooled fleet refills to its target."}
            </Typography>
        </FormDialog>;
    }

    render() {
        return <>
            <Button size="small" startIcon={<ArrowBackIcon />} onClick={this.props.onBack} sx={{ mb: 1 }}>Machines</Button>
            {this.renderHeader()}
            {this.renderServers()}
            {this.renderLog()}
            {this.renderSchedule()}
        </>;
    }
}
